import React from 'react'
import { Deal } from '../models/shared/Deal';
import { useFirebaseQueryOptions } from '../hooks/firebaseHooks';
import { COLLECTIONS } from '../utils/shared/constants';
import { useOrganization } from '../context/OrganizationContext';
import { addDoc, collection, deleteDoc, doc, where } from 'firebase/firestore';
import { useFirestore } from 'reactfire';
import DealModalSection from './DealModalSection';
import { ActiveTimeRecord, TimeRecord } from '../models/shared/TimeRecord';
import { formatDate, formatDurationFromMs, formatElapsedHms } from '../utils/date';
import { AdvancedButton } from './AdvancedButton';
import { useText } from '../context/TextContext';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import TimesheetEntryForm from './TimesheetEntryForm';
import { UserRole } from '../models/shared/UserInvitation';

interface Props {
    deal: Deal;
}
export default function DealHoursSection(props: Props) {
    const dealId = props.deal.id;
    const { orgId, orgRole } = useOrganization();
    const isWorker = orgRole === UserRole.WORKER;
    const { user } = useUser();
    const firestore = useFirestore();
    const text = useText();

    const [allHourRecords] = useFirebaseQueryOptions<TimeRecord>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}`, {
        queryConstraints: [where('dealId', '==', dealId)],
    });
    const [activeRecords] = useFirebaseQueryOptions<ActiveTimeRecord>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ACTIVE_TIME_RECORDS}`, {
        queryConstraints: [where('dealId', '==', dealId), where('email', '==', user?.email || '')],
        skip: !user?.email,
    });
    const activeRecord = activeRecords[0];
    const hourRecords = isWorker
        ? allHourRecords.filter(record => record.email === user?.email)
        : allHourRecords;

    const [showForm, setShowForm] = React.useState(false);
    const [verifyingRecord, setVerifyingRecord] = React.useState<TimeRecord | undefined>();
    const [togglingWork, setTogglingWork] = React.useState(false);
    const [now, setNow] = React.useState(() => new Date());

    React.useEffect(() => {
        if (!activeRecord?.id) {
            return;
        }

        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, [activeRecord?.id]);

    const totalDurationMs = hourRecords.reduce((acc, record) => acc + ((record.endAt || new Date()).getTime() - record.startAt.getTime()), 0);
    const elapsedMs = activeRecord ? now.getTime() - activeRecord.startAt.getTime() : 0;

    const handleStartWorking = async () => {
        if (!user?.email || !dealId) {
            return;
        }

        setTogglingWork(true);
        try {
            const newActiveRecord: ActiveTimeRecord = {
                dealId,
                email: user.email,
                startAt: new Date(),
            };
            await addDoc(collection(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ACTIVE_TIME_RECORDS}`), newActiveRecord);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setTogglingWork(false);
    };

    const handleStopWorking = async () => {
        if (!activeRecord?.id) {
            return;
        }

        setTogglingWork(true);
        try {
            await deleteDoc(doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ACTIVE_TIME_RECORDS}/${activeRecord.id}`));
            setVerifyingRecord({
                dealId: activeRecord.dealId,
                email: activeRecord.email,
                startAt: activeRecord.startAt,
                endAt: new Date(),
            });
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
        setTogglingWork(false);
    };

    if (showForm || verifyingRecord) {
        return <DealModalSection
            alwaysOpen
            title={text.newTimesheetEntry}>
            <TimesheetEntryForm
                onClose={() => { setShowForm(false); setVerifyingRecord(undefined); }}
                dealId={dealId}
                initialData={verifyingRecord}
            />

        </DealModalSection>
    }


    return (
        <DealModalSection
            title={text.trackedTime}
            alwaysOpen
        >
            <div className='d-flex flex-column gap-2'>
                <div className='d-flex gap-2 justify-content-between align-items-center'>
                    {activeRecord ? (
                        <div>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='recording-dot' />
                                <h3 className='m-0 text-danger'>{formatElapsedHms(elapsedMs)}</h3>
                            </div>
                            <div className='small text-muted'>
                                {text.workingSince} {formatDate(activeRecord.startAt, { noSeconds: true })}
                            </div>
                        </div>
                    ) : (
                        <h3 className='m-0'>{formatDurationFromMs(totalDurationMs)}</h3>
                    )}
                    <div className='d-flex gap-2'>
                        {activeRecord ? (
                            <AdvancedButton variant="outline-danger" size="sm" icon='bi-stop-circle' processing={togglingWork} onClick={handleStopWorking}>
                                {text.stopWorking}
                            </AdvancedButton>
                        ) : (
                            <AdvancedButton variant="outline-success" size="sm" icon='bi-play-circle' processing={togglingWork} onClick={handleStartWorking}>
                                {text.startWorking}
                            </AdvancedButton>
                        )}
                        <AdvancedButton variant="outline-primary" size="sm" icon='bi-clock' onClick={() => setShowForm(true)}>{text.addTime}</AdvancedButton>
                    </div>
                </div>
            </div>
        </DealModalSection>
    )
}
